import type { CSSProperties } from 'react';
import { Link, useParams, useSearch } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { DuctRun, type DuctStage, STAGE_COLOR, type StageRow } from '@monrdv/ui';
import {
  formatMoney,
  localized,
  marginPercent,
  toCents,
  type InvoiceStatus,
  type Locale,
  type MessageAuthorKind,
  type MessageTransport,
  type QuoteStatus,
  type StageKind,
} from '@monrdv/shared';
import { ApiError, request } from '../lib/api.js';
import { useCan } from '../lib/session.js';
import { RecordLink } from '../components/RecordLink.js';
import { AttachmentGrid, type Attachment } from '../components/Attachments.js';
import { VisitScheduler } from '../components/VisitScheduler.js';

/**
 * One job, on one page.
 *
 * The platform this replaces scattered a job's quotes, invoices, messages, hours and photos
 * across a dozen screens, and gave every job the same meaningless status. So: the state is
 * the headline — a duct run with its stage names, and a stage selector that is the loudest
 * control on the page, because moving work along is what a coordinator actually does here.
 *
 * Everything else is a tab, and the tab is in the URL so a colleague can be sent straight to
 * the photos or the hours.
 */

// --- assumed API shapes -----------------------------------------------------------------
// None of these endpoints exist yet. Money is a `numeric(14,2)` string, not a float; cost
// and rate fields are absent entirely when the caller lacks the capability, because
// `permissions.ts` strips COST_FIELDS / RATE_FIELDS in the serialisation layer.

/** `GET /api/pipeline-stages` */
/** `GET /api/jobs/:jobId` */
type JobRecord = {
  id: string;
  number: string;
  title: string | null;
  description: string | null;
  /** Work instructions for the technician; never shown to the customer. */
  internalNote: string | null;
  stageId: string;
  source: string;
  customer: { id: string; name: string };
  address: { line1: string; line2: string | null; city: string; region: string; postalCode: string | null; note: string | null } | null;
  scheduledStart: string | null;
  scheduledEnd: string | null;
  startedAt: string | null;
  completedAt: string | null;
  createdAt: string;
  assignees: Array<{ id: string; name: string; initials: string; isLead: boolean }>;
  services: Array<{ id: string; name: Record<string, string>; quantity: number }>;
  tags: Array<{ id: string; name: string }>;
  /** Extra trips, for the 194 jobs out of 4454 that ever needed more than one. */
  /*
   * `assignee` and `crew`, as the API has always sent them. This read `assigneeName`, a field
   * no endpoint has ever produced, so every visit rendered "Non assigné" — including the 2 556
   * of 2 599 that name somebody. The response is cast on trust, so TypeScript could not see
   * it; the smoke harness now checks these fields are present by name.
   */
  appointments: Array<{
    id: string;
    startsAt: string;
    endsAt: string | null;
    status: string | null;
    assignee: { id: string; name: string; color?: string | null } | null;
    crew: Array<{ id: string; name: string }>;
    note: string | null;
    completedAt: string | null;
    cancelledReason: string | null;
  }>;
  rollup: { quoteCount: number; invoiceCount: number; invoicedTotal: string; paidTotal: string; balance: string; laborMinutes: number; messageCount: number };
};

/** `GET /api/jobs/:jobId/money` */
type MoneyResponse = {
  quotes: Array<{ id: string; number: string; status: QuoteStatus; total: string; issuedOn: string | null; acceptedAt: string | null; expiresOn: string | null }>;
  /**
   * Customer orders — the "bon de commande" agreed before the work.
   *
   * Between the quote and the invoice: proposed, agreed, charged. Its lines are shown inline
   * because that is the whole document — what was agreed to — and it is short.
   */
  orders: Array<{
    id: string;
    number: string;
    status: string;
    orderedOn: string | null;
    total: string;
    items: Array<{ id: string; kind: string; name: string; quantity: string; unitPrice: string; total: string }>;
  }>;
  invoices: Array<{ id: string; number: string; status: InvoiceStatus; total: string; paidTotal: string; balance: string; issuedOn: string | null; dueOn: string | null }>;
  totals: {
    invoicedTotal: string;
    paidTotal: string;
    balance: string;
    /** A COST_FIELD: present only for a caller holding `billing.cost.read`. */
    costTotal?: string | null;
  };
};

/** `GET /api/jobs/:jobId/messages` */
type MessagesResponse = {
  threadId: string | null;
  /** False when these are the customer's messages rather than this job's own. */
  jobSpecific: boolean;
  items: Array<{ id: string; authorKind: MessageAuthorKind; authorName: string | null; transport: MessageTransport; body: string | null; sentAt: string; readAt: string | null; failedReason: string | null }>;
};

/** `GET /api/jobs/:jobId/time-entries` */
type TimeResponse = {
  items: Array<{
    id: string;
    user: { id: string; name: string };
    startedAt: string | null;
    endedAt: string | null;
    durationMinutes: number;
    note: string | null;
    approvedAt: string | null;
    /** RATE_FIELDS: present only for a caller holding `time.rate.read`. Wages are private. */
    hourlyRate?: string | null;
    laborCost?: string | null;
  }>;
  totalMinutes: number;
  laborCost?: string | null;
};

/** `GET /api/jobs/:jobId/attachments` — photographs and documents together. */
type AttachmentsResponse = { items: Attachment[] };

/** `GET /api/jobs/:jobId/history` */
type HistoryResponse = {
  items: Array<{
    id: string;
    at: string;
    actorName: string | null;
    kind: string;
    /** Rendered by the API, which knows the entities involved; French and English. */
    label: Record<string, string>;
    fromStage: { name: Record<string, string>; kind: StageKind } | null;
    toStage: { name: Record<string, string>; kind: StageKind } | null;
  }>;
};

/**
 * Stage colour by `kind`, never by the tenant's imported hex — the same reasoning as on the
 * jobs list. (Belongs in `packages/ui` beside `DuctRun`; duplicated here only because these
 * two screens were written in isolation.)
 */
type T = (key: string, vars?: Record<string, string | number>) => string;
type Tab = 'work' | 'money' | 'messages' | 'time' | 'photos' | 'history';
const TABS: readonly Tab[] = ['work', 'money', 'messages', 'time', 'photos', 'history'];

export function JobDetailScreen() {
  const { t, i18n } = useTranslation();
  const tr = t as T;
  const locale: Locale = i18n.language === 'en' ? 'en' : 'fr';
  const { jobId } = useParams({ from: '/jobs/$jobId' });
  const search: { search?: string; tab: Tab } = useSearch({ from: '/jobs/$jobId' });
  const tab = search.tab;
  const can = useCan();
  const client = useQueryClient();

  const job = useQuery({
    // The key the socket invalidates for this record: `['job', id]`.
    queryKey: ['job', jobId],
    queryFn: () => request<JobRecord>(`/jobs/${jobId}`),
  });

  const stages = useQuery({
    queryKey: ['pipelineStages'],
    queryFn: () => request<{ stages: StageRow[] }>('/pipeline-stages'),
    staleTime: 10 * 60_000,
  });

  const move = useMutation({
    mutationFn: (stageId: string) =>
      request<JobRecord>(`/jobs/${jobId}`, { method: 'PATCH', body: JSON.stringify({ stageId }) }),
    onMutate: async (stageId) => {
      await client.cancelQueries({ queryKey: ['job', jobId] });
      const previous = client.getQueryData<JobRecord>(['job', jobId]);
      // Write it locally first so the copper node eases across to the new stage on the
      // click, not on the round trip. This is the one place motion carries meaning.
      if (previous) client.setQueryData<JobRecord>(['job', jobId], { ...previous, stageId });
      return { previous };
    },
    onError: (_error, _stageId, context) => {
      if (context?.previous) client.setQueryData(['job', jobId], context.previous);
    },
    onSettled: () => {
      // The board and the list both order by stage, so both are now wrong.
      void client.invalidateQueries({ queryKey: ['job', jobId] });
      void client.invalidateQueries({ queryKey: ['jobs'] });
      void client.invalidateQueries({ queryKey: ['board'] });
    },
  });

  if (job.isPending) return <p style={S.quiet}>{tr('common.loading')}</p>;

  if (job.isError) {
    const notFound = job.error instanceof ApiError && job.error.status === 404;
    return (
      <div style={S.failure} role="alert">
        <span>{notFound ? tr('errors.notFound') : tr('errors.generic')}</span>
        {!notFound && (
          <button type="button" style={S.button} onClick={() => void job.refetch()}>
            {tr('common.retry')}
          </button>
        )}
      </div>
    );
  }

  const record = job.data;
  const stageRows = [...(stages.data?.stages ?? [])].sort((a, b) => a.position - b.position);
  const ductStages: DuctStage[] = stageRows.map((s) => ({
    id: s.id,
    name: localized(s.name, locale),
    kind: s.kind,
    color: STAGE_COLOR[s.kind],
  }));
  const slot = formatWindow(record.scheduledStart, record.scheduledEnd, locale);

  return (
    <div style={S.screen}>
      <header style={S.header}>
        <div style={S.identity}>
          <span className="label">{tr('job.number')}</span>
          <h1 className="tnum" style={S.number}>
            {record.number}
          </h1>
          {record.title && <p style={S.jobTitle}>{record.title}</p>}

          <Link to="/customers/$customerId" params={{ customerId: record.customer.id }} style={S.customerLink}>
            {record.customer.name}
          </Link>

          {record.address ? (
            <address style={S.address}>
              {record.address.line1}
              {record.address.line2 ? `, ${record.address.line2}` : ''}
              <br />
              {record.address.city} {record.address.region}
              {record.address.postalCode && <span className="tnum"> {record.address.postalCode}</span>}
            </address>
          ) : (
            <p style={S.absent}>{tr('customer.address')} —</p>
          )}

          {/* Gate codes and "ring twice" decide whether a technician gets in at all, so the
              access note is copper — the one warm accent, used where attention is earned. */}
          {record.address?.note && (
            <p style={S.accessNote}>
              <span className="label">{tr('customer.accessNote')}</span>
              {record.address.note}
            </p>
          )}
        </div>

        <div style={S.headerRight}>
          <StageControl
            stages={stageRows}
            currentStageId={record.stageId}
            locale={locale}
            disabled={!can('job.write')}
            pending={move.isPending}
            failed={move.isError}
            onMove={(stageId) => move.mutate(stageId)}
            t={tr}
          />

          <dl style={S.readings}>
            <Reading label={tr('job.scheduled')}>
              {slot ? <span className="dimension tnum">{slot}</span> : <span style={S.absent}>{tr('job.unscheduled')}</span>}
            </Reading>
            <Reading label={tr('job.assignees')}>
              {record.assignees.length === 0 ? (
                // Thousands of jobs sit here waiting to be dispatched; it is a state, not a fault.
                <span style={S.absent}>{tr('job.unassigned')}</span>
              ) : (
                <span style={S.avatars}>
                  {record.assignees.map((person) => (
                    <span
                      key={person.id}
                      style={{ ...S.avatar, ...(person.isLead ? S.avatarLead : null) }}
                      title={person.name}
                      aria-label={person.name}
                    >
                      {person.initials}
                    </span>
                  ))}
                </span>
              )}
            </Reading>
            {toCents(record.rollup.balance) > 0 && (
              <Reading label={tr('job.balanceDue')}>
                <span className="money">{formatMoney(toCents(record.rollup.balance), locale)}</span>
              </Reading>
            )}
          </dl>
        </div>
      </header>

      {/* The signature element, at full width with its stage names: one glance answers
          "where is this?" without a legend. */}
      <div style={S.run}>
        {/* No `label`: the component's own "Étape : …" is the right announcement here, and
            the stage names are on screen beneath the run anyway. */}
        <DuctRun stages={ductStages} currentStageId={record.stageId} variant="detail" />
      </div>

      {/* Tabs are links, not buttons: every one is a shareable URL, and the browser handles
          focus and keyboard for free. */}
      <nav style={S.tabs} aria-label={tr('job.tabsLabel')}>
        {TABS.map((name) => (
          <Link
            key={name}
            to="/jobs/$jobId"
            params={{ jobId }}
            // Spread rather than replace: switching tabs must not close an open search overlay.
            search={{ ...search, tab: name }}
            aria-current={tab === name ? 'page' : undefined}
            style={{ ...S.tab, ...(tab === name ? S.tabOn : null) }}
          >
            {tr(`job.tab.${name}`)}
            {name === 'messages' && record.rollup.messageCount > 0 && (
              <span className="tnum" style={S.tabCount}>
                {record.rollup.messageCount}
              </span>
            )}
          </Link>
        ))}
      </nav>

      <section aria-label={tr(`job.tab.${tab}`)} style={S.panel}>
        {tab === 'work' && <WorkTab job={record} locale={locale} t={tr} />}
        {tab === 'money' && <MoneyTab jobId={jobId} locale={locale} t={tr} />}
        {tab === 'messages' && <MessagesTab jobId={jobId} locale={locale} t={tr} />}
        {tab === 'time' && <TimeTab jobId={jobId} locale={locale} t={tr} />}
        {tab === 'photos' && <PhotosTab jobId={jobId} t={tr} />}
        {tab === 'history' && <HistoryTab jobId={jobId} locale={locale} t={tr} />}
      </section>
    </div>
  );
}

/**
 * Moving the job along.
 *
 * Deliberately the heaviest control on the page: a stage change is the action this screen
 * exists for. It is a select over the tenant's own stages rather than a fixed list, because
 * the three businesses genuinely run three different pipelines.
 */
function StageControl({
  stages,
  currentStageId,
  locale,
  disabled,
  pending,
  failed,
  onMove,
  t,
}: {
  stages: StageRow[];
  currentStageId: string;
  locale: Locale;
  disabled: boolean;
  pending: boolean;
  failed: boolean;
  onMove: (stageId: string) => void;
  t: T;
}) {
  const current = stages.find((s) => s.id === currentStageId);

  return (
    <div style={S.stageBox}>
      <label className="label" htmlFor="job-stage" style={S.stageLabel}>
        {t('job.moveStage')}
      </label>
      <div style={S.stageRow}>
        <span aria-hidden="true" style={{ ...S.stageDot, background: current ? STAGE_COLOR[current.kind] : 'var(--border-strong)' }} />
        <select
          id="job-stage"
          value={currentStageId}
          disabled={disabled || pending}
          onChange={(e) => onMove(e.target.value)}
          style={S.stageSelect}
        >
          {stages.map((stage) => (
            <option key={stage.id} value={stage.id}>
              {localized(stage.name, locale)}
            </option>
          ))}
        </select>
      </div>
      {/* aria-live so the outcome reaches a screen reader without stealing focus from the
          select the coordinator is still holding. */}
      <span aria-live="polite" style={S.stageStatus}>
        {pending && t('job.moving')}
        {failed && t('errors.generic')}
      </span>
    </div>
  );
}

function WorkTab({ job, locale, t }: { job: JobRecord; locale: Locale; t: T }) {
  return (
    <div style={S.columns}>
      <div style={S.stack}>
        <Panel title={t('job.description')}>
          {job.description ? <p style={S.prose}>{job.description}</p> : <p style={S.absent}>—</p>}
        </Panel>

        {job.internalNote && (
          <Panel title={t('job.internalNote')}>
            <p style={S.prose}>{job.internalNote}</p>
          </Panel>
        )}

        {/*
          Always rendered, where it used to appear only when visits existed — which meant the
          one job that most needed a technician sent was the one offering no way to send one.
        */}
        <Panel title={t('job.appointments')}>
          <VisitScheduler jobId={job.id} visits={job.appointments} locale={locale} formatWindow={formatWindow} />
        </Panel>
      </div>

      <div style={S.stack}>
        <Panel title={t('job.services')}>
          {job.services.length === 0 ? (
            <p style={S.absent}>—</p>
          ) : (
            <ul style={S.chips}>
              {job.services.map((service) => (
                <li key={service.id} style={S.chip}>
                  {localized(service.name, locale)}
                  {service.quantity > 1 && <span className="tnum"> ×{service.quantity}</span>}
                </li>
              ))}
            </ul>
          )}
        </Panel>

        <Panel title={t('job.milestones')}>
          <dl style={S.facts}>
            <Fact label={t('job.createdOn')} value={formatDay(job.createdAt, locale)} numeric="tnum" />
            <Fact
              label={t('job.startedAt')}
              value={job.startedAt ? formatMoment(job.startedAt, locale) : '—'}
              numeric="tnum"
            />
            <Fact
              label={t('job.completedAt')}
              value={job.completedAt ? formatMoment(job.completedAt, locale) : '—'}
              numeric="tnum"
            />
            <Fact label={t('job.source')} value={t(`job.sourceKind.${job.source}`)} />
          </dl>
        </Panel>

        {job.tags.length > 0 && (
          <Panel title={t('job.tags')}>
            <ul style={S.chips}>
              {job.tags.map((tag) => (
                <li key={tag.id} style={S.chip}>
                  {tag.name}
                </li>
              ))}
            </ul>
          </Panel>
        )}
      </div>
    </div>
  );
}

/**
 * Money on this job.
 *
 * Quotes and invoices, then the reading that matters to an owner: margin. Cost and margin
 * are shown only to a session holding `billing.cost.read` — a technician must never learn
 * what a part cost the company. The server strips COST_FIELDS regardless; this check keeps
 * the interface from asking for something it may not display.
 */
function MoneyTab({ jobId, locale, t }: { jobId: string; locale: Locale; t: T }) {
  const can = useCan();
  const money = useQuery({
    queryKey: ['job', jobId, 'money'],
    queryFn: () => request<MoneyResponse>(`/jobs/${jobId}/money`),
  });

  if (money.isPending) return <p style={S.quiet}>{t('common.loading')}</p>;
  if (money.isError) return <p style={S.quiet}>{t('errors.generic')}</p>;

  const { quotes, orders, invoices, totals } = money.data;
  const revenue = toCents(totals.invoicedTotal);
  const costVisible = can('billing.cost.read') && totals.costTotal != null;
  const cost = costVisible ? toCents(totals.costTotal) : 0;
  const percent = costVisible ? marginPercent(revenue, cost) : null;

  if (quotes.length === 0 && orders.length === 0 && invoices.length === 0) {
    return <p style={S.quiet}>{t('job.empty.money')}</p>;
  }

  return (
    <div style={S.stack}>
      {quotes.length > 0 && (
        <Panel title={t('quote.many')}>
          <table style={S.table}>
            <thead>
              <tr>
                <th scope="col" style={S.th}>{t('job.money.number')}</th>
                <th scope="col" style={S.th}>{t('job.money.status')}</th>
                <th scope="col" style={S.th}>{t('job.money.issued')}</th>
                <th scope="col" style={{ ...S.th, textAlign: 'right' }}>{t('invoice.total')}</th>
              </tr>
            </thead>
            <tbody>
              {quotes.map((quote) => (
                <tr key={quote.id} style={S.tr}>
                  <td style={S.td}>
                    {/* Opens per the reader's own preference — same page, side panel or new
                        tab — because RecordLink is the single place that decision is made. */}
                    <RecordLink kind="quote" id={quote.id} className="cell__link">
                      <span className="tnum">{quote.number}</span>
                    </RecordLink>
                  </td>
                  <td style={S.td}>{t(`quote.status.${quote.status}`)}</td>
                  <td style={S.td}>
                    <span className="tnum">{quote.issuedOn ? formatDay(quote.issuedOn, locale) : '—'}</span>
                  </td>
                  <td style={{ ...S.td, textAlign: 'right' }}>
                    <span className="money">{formatMoney(toCents(quote.total), locale)}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
      )}

      {/* Sits between the quote and the invoice, and is laid out in that order on purpose:
          what we proposed, what they agreed to, what we charged. */}
      {orders.length > 0 && (
        <Panel title="Bons de commande">
          {orders.map((order) => (
            <div key={order.id} style={{ marginBottom: 12 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 4 }}>
                <span className="tnum" style={{ fontWeight: 600 }}>{order.number}</span>
                <span style={S.quiet}>
                  {order.orderedOn ? formatDay(order.orderedOn, locale) : '—'}
                </span>
                <span className="money" style={{ marginLeft: 'auto' }}>
                  {formatMoney(toCents(order.total), locale)}
                </span>
              </div>
              <table style={S.table}>
                <tbody>
                  {order.items.map((item) => (
                    <tr key={item.id} style={S.tr}>
                      <td style={S.td}>
                        {item.name}
                        {/* Goods sold versus work performed: the two bill differently, and the
                            source does distinguish them. */}
                        {item.kind === 'goods' && <span style={S.quiet}> · marchandise</span>}
                      </td>
                      <td style={{ ...S.td, textAlign: 'right' }}>
                        <span className="tnum">{item.quantity}</span>
                      </td>
                      <td style={{ ...S.td, textAlign: 'right' }}>
                        <span className="money">{formatMoney(toCents(item.total), locale)}</span>
                      </td>
                    </tr>
                  ))}
                  {order.items.length === 0 && (
                    <tr style={S.tr}>
                      <td style={{ ...S.td, ...S.quiet }}>Aucune ligne enregistrée</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          ))}
        </Panel>
      )}

      {invoices.length > 0 && (
        <Panel title={t('invoice.many')}>
          <table style={S.table}>
            <thead>
              <tr>
                <th scope="col" style={S.th}>{t('job.money.number')}</th>
                <th scope="col" style={S.th}>{t('job.money.status')}</th>
                <th scope="col" style={S.th}>{t('job.money.due')}</th>
                <th scope="col" style={{ ...S.th, textAlign: 'right' }}>{t('invoice.total')}</th>
                <th scope="col" style={{ ...S.th, textAlign: 'right' }}>{t('invoice.balance')}</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((invoice) => (
                <tr key={invoice.id} style={S.tr}>
                  <td style={S.td}>
                    <RecordLink kind="invoice" id={invoice.id} className="cell__link">
                      <span className="tnum">{invoice.number}</span>
                    </RecordLink>
                  </td>
                  <td style={S.td}>{t(`invoice.status.${invoice.status}`)}</td>
                  <td style={S.td}>
                    <span className="tnum">{invoice.dueOn ? formatDay(invoice.dueOn, locale) : '—'}</span>
                  </td>
                  <td style={{ ...S.td, textAlign: 'right' }}>
                    <span className="money">{formatMoney(toCents(invoice.total), locale)}</span>
                  </td>
                  <td style={{ ...S.td, textAlign: 'right' }}>
                    {toCents(invoice.balance) > 0 ? (
                      <span className="money">{formatMoney(toCents(invoice.balance), locale)}</span>
                    ) : (
                      <span style={S.paid}>{t('invoice.status.paid')}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
      )}

      <Panel title={t('job.money.summary')}>
        <dl style={S.facts}>
          <Fact label={t('invoice.total')} value={formatMoney(revenue, locale)} numeric="money" />
          <Fact label={t('invoice.paid')} value={formatMoney(toCents(totals.paidTotal), locale)} numeric="money" />
          <Fact label={t('invoice.balance')} value={formatMoney(toCents(totals.balance), locale)} numeric="money" />
          {/* Nothing takes the place of a hidden margin: a "you cannot see this" line
              advertises the number's existence and invites asking a colleague to read it out. */}
          {costVisible && (
            <>
              <Fact label={t('job.cost')} value={formatMoney(cost, locale)} numeric="money" />
              <Fact
                label={t('job.margin')}
                value={`${formatMoney(revenue - cost, locale)}${percent === null ? '' : ` · ${formatPercent(percent, locale)}`}`}
                numeric="money"
              />
            </>
          )}
        </dl>
      </Panel>
    </div>
  );
}

function MessagesTab({ jobId, locale, t }: { jobId: string; locale: Locale; t: T }) {
  const messages = useQuery({
    // `['messages', …]` is what a `message` invalidation hits, so a reply arriving from the
    // office appears here without a refresh.
    queryKey: ['messages', 'job', jobId],
    queryFn: () => request<MessagesResponse>(`/jobs/${jobId}/messages`),
  });

  if (messages.isPending) return <p style={S.quiet}>{t('common.loading')}</p>;
  if (messages.isError) return <p style={S.quiet}>{t('errors.generic')}</p>;
  if (messages.data.items.length === 0) return <p style={S.quiet}>{t('job.empty.messages')}</p>;

  return (
    <div style={S.stack}>
      {/*
        Said plainly when these are the customer's messages rather than this job's. Showing a
        customer's whole exchange under a job's Messages tab without saying so would invite
        somebody to read a sentence about last winter's unit as though it were about this work.
      */}
      {messages.data.threadId && !messages.data.jobSpecific && (
        <p style={S.quiet}>{t('job.customerConversation')}</p>
      )}
      {messages.data.threadId && (
        <Link to="/inbox" search={{ thread: messages.data.threadId }} style={S.inlineLink}>
          {t('job.openThread')}
        </Link>
      )}
      <ul style={S.list}>
        {messages.data.items.map((message) => (
          <li key={message.id} style={{ ...S.message, ...(message.authorKind === 'customer' ? S.messageIn : null) }}>
            <span style={S.messageHead}>
              <span style={S.messageAuthor}>{message.authorName ?? t(`job.message.${message.authorKind}`)}</span>
              <span className="label">{t(`job.message.transport.${message.transport}`)}</span>
              <span className="tnum" style={S.subtle}>
                {formatMoment(message.sentAt, locale)}
              </span>
            </span>
            <span style={S.prose}>{message.body}</span>
            {message.failedReason && <span style={S.failed}>{message.failedReason}</span>}
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Hours on this job.
 *
 * Rates and the labour cost they imply are wages, so they appear only for `time.rate.read`
 * (RATE_FIELDS). Everyone with access to the job may see the hours themselves — that is what
 * makes the total a useful reading rather than a payroll document.
 */
function TimeTab({ jobId, locale, t }: { jobId: string; locale: Locale; t: T }) {
  const can = useCan();
  const rates = can('time.rate.read');
  const time = useQuery({
    queryKey: ['timeEntries', 'job', jobId],
    queryFn: () => request<TimeResponse>(`/jobs/${jobId}/time-entries`),
  });

  if (time.isPending) return <p style={S.quiet}>{t('common.loading')}</p>;
  if (time.isError) return <p style={S.quiet}>{t('errors.generic')}</p>;
  if (time.data.items.length === 0) return <p style={S.quiet}>{t('job.empty.time')}</p>;

  const { items, totalMinutes } = time.data;

  return (
    <Panel title={t('nav.timesheets')}>
      <table style={S.table}>
        <thead>
          <tr>
            <th scope="col" style={S.th}>{t('job.assignees')}</th>
            <th scope="col" style={S.th}>{t('job.time.window')}</th>
            <th scope="col" style={{ ...S.th, textAlign: 'right' }}>{t('job.time.duration')}</th>
            {rates && <th scope="col" style={{ ...S.th, textAlign: 'right' }}>{t('job.time.rate')}</th>}
            <th scope="col" style={S.th}>{t('job.time.note')}</th>
          </tr>
        </thead>
        <tbody>
          {items.map((entry) => (
            <tr key={entry.id} style={S.tr}>
              <td style={S.td}><PersonLink id={entry.user.id} name={entry.user.name} /></td>
              <td style={S.td}>
                <span className="tnum">{formatWindow(entry.startedAt, entry.endedAt, locale) ?? '—'}</span>
              </td>
              <td style={{ ...S.td, textAlign: 'right' }}>
                <span className="tnum">{formatDuration(entry.durationMinutes, locale)}</span>
              </td>
              {rates && (
                <td style={{ ...S.td, textAlign: 'right' }}>
                  <span className="money">{entry.hourlyRate ? formatMoney(toCents(entry.hourlyRate), locale) : '—'}</span>
                </td>
              )}
              <td style={S.td}>
                <span style={S.subtle}>{entry.note}</span>
                {entry.approvedAt && <span style={S.approved}>{t('job.time.approved')}</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* A logged duration is a measured span, which is exactly what the dimension rule is
          for — the one place a hairline-with-ticks is information rather than decoration. */}
      <p className="dimension" style={S.total}>
        <span className="tnum">{t('time.hoursLogged', { hours: (totalMinutes / 60).toFixed(1) })}</span>
        {rates && time.data.laborCost != null && (
          <span className="money">{formatMoney(toCents(time.data.laborCost), locale)}</span>
        )}
      </p>
    </Panel>
  );
}

/**
 * Everything attached to this job, photographs and documents in one grid.
 *
 * It used to ask for `kind=image`, which quietly hid the reports, quotes and manufacturer
 * documentation that are also filed against a job — eight thousand PDFs in the imported
 * history, none of them reachable from the screen they belong to. They are one list now,
 * because somebody looking for "the paperwork on this job" does not know in advance whether
 * what they want was photographed or scanned.
 */
/**
 * A person, opened on the team screen.
 *
 * A plain `Link` rather than `RecordLink`: people are not a peekable record — there is no side
 * panel that renders one — and pretending otherwise would give a control that opens an empty
 * drawer. The team screen already takes `?person=`, so this lands on them directly.
 */
function PersonLink({ id, name }: { id: string; name: string }) {
  return (
    <Link to="/team" search={{ person: id }} className="personlink">
      {name}
    </Link>
  );
}

function PhotosTab({ jobId, t }: { jobId: string; t: T }) {
  const files = useQuery({
    queryKey: ['job', jobId, 'attachments'],
    queryFn: () => request<AttachmentsResponse>(`/jobs/${jobId}/attachments`),
  });

  if (files.isPending) return <p style={S.quiet}>{t('common.loading')}</p>;
  if (files.isError) return <p style={S.quiet}>{t('errors.generic')}</p>;

  return <AttachmentGrid items={files.data.items} emptyMessage={t('job.empty.photos')} />;
}

/**
 * What happened to this job.
 *
 * The row labels are rendered by the API because they name entities it knows and the client
 * does not; stage moves additionally carry both stages, so the movement is visible as colour
 * rather than only as prose.
 */
function HistoryTab({ jobId, locale, t }: { jobId: string; locale: Locale; t: T }) {
  const history = useQuery({
    queryKey: ['job', jobId, 'history'],
    queryFn: () => request<HistoryResponse>(`/jobs/${jobId}/history`),
  });

  if (history.isPending) return <p style={S.quiet}>{t('common.loading')}</p>;
  if (history.isError) return <p style={S.quiet}>{t('errors.generic')}</p>;
  if (history.data.items.length === 0) return <p style={S.quiet}>{t('job.empty.history')}</p>;

  return (
    <ol style={S.timeline}>
      {history.data.items.map((event) => (
        <li key={event.id} style={S.event}>
          <span className="tnum" style={S.eventWhen}>
            {formatMoment(event.at, locale)}
          </span>
          <span style={S.eventBody}>
            <span>{localized(event.label, locale)}</span>
            {event.toStage && (
              <span style={S.stagePair}>
                {event.fromStage && (
                  <>
                    <StagePill stage={event.fromStage} locale={locale} muted />
                    <span aria-hidden="true">→</span>
                  </>
                )}
                <StagePill stage={event.toStage} locale={locale} />
              </span>
            )}
            {event.actorName && <span style={S.subtle}>{event.actorName}</span>}
          </span>
        </li>
      ))}
    </ol>
  );
}

function StagePill({
  stage,
  locale,
  muted,
}: {
  stage: { name: Record<string, string>; kind: StageKind };
  locale: Locale;
  muted?: boolean;
}) {
  return (
    <span
      style={{
        ...S.pill,
        color: muted ? 'var(--text-secondary)' : STAGE_COLOR[stage.kind],
        borderColor: muted ? 'var(--border-strong)' : STAGE_COLOR[stage.kind],
      }}
    >
      {localized(stage.name, locale)}
    </span>
  );
}

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section style={S.panelBox}>
      <h2 className="label" style={S.panelTitle}>
        {title}
      </h2>
      {children}
    </section>
  );
}

function Reading({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={S.reading}>
      <dt className="label">{label}</dt>
      <dd style={S.readingValue}>{children}</dd>
    </div>
  );
}

/**
 * One labelled reading. `numeric` names the tabular class directly — `money` for amounts,
 * `tnum` for dates and counts — because a column of figures that does not align on the
 * decimal cannot be scanned.
 */
function Fact({ label, value, numeric }: { label: string; value: string; numeric?: 'money' | 'tnum' }) {
  return (
    <div style={S.fact}>
      <dt className="label">{label}</dt>
      <dd style={S.factValue} className={numeric}>
        {value}
      </dd>
    </div>
  );
}

/**
 * Date and duration formatting.
 *
 * Formatters are cached per locale rather than constructed per row: a job with fifty time
 * entries would otherwise build a hundred `Intl` instances on every render.
 */
const formatters = new Map<string, Intl.DateTimeFormat>();
function formatter(locale: Locale, key: string, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const id = `${locale}:${key}`;
  let existing = formatters.get(id);
  if (!existing) {
    existing = new Intl.DateTimeFormat(locale === 'fr' ? 'fr-CA' : 'en-CA', options);
    formatters.set(id, existing);
  }
  return existing;
}

function formatWindow(startIso: string | null, endIso: string | null, locale: Locale): string | null {
  if (!startIso) return null;
  const start = new Date(startIso);
  const day = formatter(locale, 'day', { weekday: 'short', day: 'numeric', month: 'short' }).format(start);
  const clock = formatter(locale, 'clock', { hour: '2-digit', minute: '2-digit' });
  if (!endIso) return `${day} ${clock.format(start)}`;
  return `${day} ${clock.format(start)}–${clock.format(new Date(endIso))}`;
}

const formatDay = (iso: string, locale: Locale): string =>
  formatter(locale, 'date', { day: 'numeric', month: 'short', year: 'numeric' }).format(new Date(iso));

const formatMoment = (iso: string, locale: Locale): string =>
  formatter(locale, 'moment', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }).format(new Date(iso));

/** `2 h 30` in French, `2h 30m` in English. Durations are read, not calculated, so keep them short. */
function formatDuration(minutes: number, locale: Locale): string {
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (locale === 'fr') return rest ? `${hours} h ${String(rest).padStart(2, '0')}` : `${hours} h`;
  return rest ? `${hours}h ${rest}m` : `${hours}h`;
}

const percentFormatters = new Map<Locale, Intl.NumberFormat>();
function formatPercent(value: number, locale: Locale): string {
  let existing = percentFormatters.get(locale);
  if (!existing) {
    existing = new Intl.NumberFormat(locale === 'fr' ? 'fr-CA' : 'en-CA', { style: 'percent', maximumFractionDigits: 1 });
    percentFormatters.set(locale, existing);
  }
  return existing.format(value / 100);
}

/** Every value below resolves to a token in `tokens.css`; no colour is chosen by hand. */
const S = {
  screen: { display: 'flex', flexDirection: 'column', gap: 'var(--space-4)', padding: 'var(--space-6)', minWidth: 0 },
  header: { display: 'flex', flexWrap: 'wrap', gap: 'var(--space-6)', justifyContent: 'space-between', alignItems: 'flex-start' },
  identity: { display: 'flex', flexDirection: 'column', gap: 'var(--space-1)', minWidth: 260 },
  number: { fontSize: 'var(--text-2xl)', lineHeight: 1.1 },
  jobTitle: { margin: 0, fontSize: 'var(--text-base)', color: 'var(--text-primary)' },
  customerLink: { color: 'var(--accent)', fontWeight: 600, fontSize: 'var(--text-base)', textDecoration: 'none' },
  address: { fontStyle: 'normal', fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' },
  accessNote: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--space-1)',
    margin: 'var(--space-1) 0 0',
    padding: 'var(--space-2) var(--space-3)',
    background: 'var(--accent-warm-wash)',
    borderLeft: '2px solid var(--accent-warm)',
    borderRadius: 'var(--radius-sm)',
    fontSize: 'var(--text-sm)',
  },

  headerRight: { display: 'flex', flexDirection: 'column', gap: 'var(--space-3)', alignItems: 'flex-start' },
  stageBox: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--space-1)',
    padding: 'var(--space-3)',
    background: 'var(--surface-raised)',
    border: '1px solid var(--accent)',
    borderRadius: 'var(--radius-md)',
    boxShadow: 'var(--shadow-md)',
    minWidth: 280,
  },
  stageLabel: { color: 'var(--accent)' },
  stageRow: { display: 'flex', alignItems: 'center', gap: 'var(--space-2)' },
  stageDot: { width: 10, height: 10, borderRadius: 'var(--radius-full)', flex: '0 0 auto' },
  stageSelect: {
    flex: 1,
    minHeight: 40,
    padding: 'var(--space-2) var(--space-3)',
    font: '600 var(--text-base)/1.2 var(--font-body)',
    color: 'var(--text-primary)',
    background: 'var(--surface-raised)',
    border: '1px solid var(--border-strong)',
    borderRadius: 'var(--radius-sm)',
  },
  stageStatus: { minHeight: '1em', fontSize: 'var(--text-xs)', color: 'var(--text-secondary)' },

  readings: { display: 'flex', flexWrap: 'wrap', gap: 'var(--space-5)', margin: 0 },
  reading: { display: 'flex', flexDirection: 'column', gap: 'var(--space-1)', minWidth: 160 },
  readingValue: { margin: 0, fontSize: 'var(--text-sm)' },

  run: {
    padding: 'var(--space-4)',
    background: 'var(--surface-raised)',
    border: '1px solid var(--border-hairline)',
    borderRadius: 'var(--radius-md)',
    boxShadow: 'var(--shadow-sm)',
  },

  tabs: { display: 'flex', gap: 'var(--space-1)', borderBottom: '1px solid var(--border-strong)', flexWrap: 'wrap' },
  tab: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 'var(--space-2)',
    padding: 'var(--space-2) var(--space-3)',
    marginBottom: -1,
    color: 'var(--text-secondary)',
    font: '600 var(--text-sm)/1 var(--font-body)',
    textDecoration: 'none',
    borderBottom: '2px solid transparent',
  },
  tabOn: { color: 'var(--text-primary)', borderBottomColor: 'var(--accent-warm)' },
  tabCount: { fontSize: 'var(--text-2xs)', color: 'var(--text-muted)' },
  panel: { minWidth: 0 },

  columns: { display: 'grid', gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr)', gap: 'var(--space-4)' },
  stack: { display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' },
  panelBox: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--space-2)',
    padding: 'var(--space-4)',
    background: 'var(--surface-raised)',
    border: '1px solid var(--border-hairline)',
    borderRadius: 'var(--radius-md)',
    boxShadow: 'var(--shadow-sm)',
  },
  panelTitle: { margin: 0 },
  prose: { margin: 0, fontSize: 'var(--text-sm)', lineHeight: 1.55, whiteSpace: 'pre-wrap' },

  facts: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 'var(--space-3)', margin: 0 },
  fact: { display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' },
  factValue: { margin: 0, fontSize: 'var(--text-sm)', fontWeight: 600 },

  list: { listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' },
  listRow: { display: 'flex', flexDirection: 'column', gap: 'var(--space-1)', fontSize: 'var(--text-sm)' },
  chips: { listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' },
  chip: {
    padding: 'var(--space-1) var(--space-2)',
    background: 'var(--surface-sunken)',
    border: '1px solid var(--border-hairline)',
    borderRadius: 'var(--radius-sm)',
    fontSize: 'var(--text-xs)',
  },
  pill: {
    padding: '0 var(--space-2)',
    border: '1px solid',
    borderRadius: 'var(--radius-sm)',
    fontSize: 'var(--text-2xs)',
    letterSpacing: 'var(--tracking-label)',
    textTransform: 'uppercase',
    fontWeight: 600,
  },

  table: { width: '100%', borderCollapse: 'collapse', fontSize: 'var(--text-sm)' },
  th: {
    textAlign: 'left',
    padding: 'var(--space-2)',
    borderBottom: '1px solid var(--border-strong)',
    font: '600 var(--text-2xs)/1 var(--font-body)',
    letterSpacing: 'var(--tracking-label)',
    textTransform: 'uppercase',
    color: 'var(--text-secondary)',
    whiteSpace: 'nowrap',
  },
  tr: { borderBottom: '1px solid var(--border-hairline)' },
  td: { padding: 'var(--space-2)', verticalAlign: 'top' },
  total: { margin: 'var(--space-2) 0 0', gap: 'var(--space-3)' },
  paid: { color: 'var(--state-positive)', fontSize: 'var(--text-xs)', fontWeight: 600 },
  approved: { display: 'block', color: 'var(--state-positive)', fontSize: 'var(--text-2xs)', fontWeight: 600 },

  message: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--space-1)',
    padding: 'var(--space-3)',
    background: 'var(--surface-raised)',
    border: '1px solid var(--border-hairline)',
    borderRadius: 'var(--radius-md)',
    fontSize: 'var(--text-sm)',
  },
  messageIn: { background: 'var(--surface-sunken)', borderColor: 'var(--border-strong)' },
  messageHead: { display: 'flex', alignItems: 'baseline', gap: 'var(--space-2)', flexWrap: 'wrap' },
  messageAuthor: { fontWeight: 600 },
  failed: { color: 'var(--state-negative)', fontSize: 'var(--text-xs)' },

  gallery: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
    gap: 'var(--space-3)',
  },
  photo: { display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' },
  photoImg: {
    width: '100%',
    height: 150,
    objectFit: 'cover',
    background: 'var(--surface-sunken)',
    border: '1px solid var(--border-hairline)',
    borderRadius: 'var(--radius-sm)',
  },
  photoMeta: { display: 'flex', flexDirection: 'column', fontSize: 'var(--text-xs)' },

  timeline: { listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' },
  event: { display: 'grid', gridTemplateColumns: '150px 1fr', gap: 'var(--space-3)', fontSize: 'var(--text-sm)' },
  eventWhen: { color: 'var(--text-secondary)', fontSize: 'var(--text-xs)' },
  eventBody: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 'var(--space-2)' },
  stagePair: { display: 'inline-flex', alignItems: 'center', gap: 'var(--space-2)', color: 'var(--text-muted)' },

  avatars: { display: 'inline-flex', alignItems: 'center', gap: 'var(--space-1)' },
  avatar: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 24,
    height: 24,
    borderRadius: 'var(--radius-full)',
    background: 'var(--surface-sunken)',
    border: '1px solid var(--border-strong)',
    font: '600 var(--text-2xs)/1 var(--font-body)',
    color: 'var(--text-secondary)',
  },
  avatarLead: { background: 'var(--accent-wash)', borderColor: 'var(--accent)', color: 'var(--accent)' },

  inlineLink: { color: 'var(--accent)', fontSize: 'var(--text-sm)', fontWeight: 600, textDecoration: 'none' },
  subtle: { fontSize: 'var(--text-xs)', color: 'var(--text-secondary)' },
  absent: { color: 'var(--text-muted)', fontSize: 'var(--text-sm)' },
  quiet: { color: 'var(--text-secondary)', fontSize: 'var(--text-sm)' },
  button: {
    padding: 'var(--space-2) var(--space-3)',
    background: 'var(--surface-sunken)',
    border: '1px solid var(--border-strong)',
    borderRadius: 'var(--radius-sm)',
    color: 'var(--text-primary)',
    font: '600 var(--text-sm)/1 var(--font-body)',
    cursor: 'pointer',
  },
  failure: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--space-3)',
    margin: 'var(--space-6)',
    padding: 'var(--space-3)',
    background: 'var(--state-negative-wash)',
    border: '1px solid var(--state-negative)',
    borderRadius: 'var(--radius-sm)',
    fontSize: 'var(--text-sm)',
  },
} satisfies Record<string, CSSProperties>;
